import { z } from 'zod';

/**
 * Work hours of a client (D-121, W2-3-3): the SLA clock of the new watch rules. Workdays = `days` (0 = Sunday .. 6 = Saturday),
 * never Shabbat or yom tov; a Friday / erev yom tov counts until `fridayEnd`. Delivery happens only inside [start, end)
 * ([start, fridayEnd) on a Friday / erev yom tov). Stored on AlertSettings.workCalendar; the nearest scope's whole object wins.
 */
export interface WorkCalendar {
  /** 0 = Sunday .. 6 = Saturday */
  days: number[];
  /** HH:mm */
  start: string;
  /** HH:mm */
  end: string;
  /** HH:mm - the end of a Friday / erev yom tov */
  fridayEnd: string;
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const minutesOf = (hhmm: string): number => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

export const WorkCalendarSchema: z.ZodType<WorkCalendar> = z
  .object({
    days: z
      .array(z.number().int().min(0).max(6))
      .min(1)
      .max(7)
      .refine((d) => new Set(d).size === d.length, 'days must be unique'),
    start: z.string().regex(HHMM),
    end: z.string().regex(HHMM),
    fridayEnd: z.string().regex(HHMM),
  })
  .refine((c) => minutesOf(c.start) < minutesOf(c.end), { message: 'start must be before end', path: ['end'] });

/** Sun-Fri, 09:00-19:00, Friday / erev chag until 13:00 (D-121) */
export const DEFAULT_WORK_CALENDAR: WorkCalendar = { days: [0, 1, 2, 3, 4, 5], start: '09:00', end: '19:00', fridayEnd: '13:00' };
